import produce from "immer";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  TextField,
  Typography
} from "@mui/material";

import { bindActionCreators } from "redux";
import { tl } from "../../../../components/translate";
import { resourceCentreActions } from "../../../../actions";
import { deleteClientQueueNumbers } from "../../../../api/clientQueueNumber";
import useCurrentResourceCentre from "../../../../hooks/useCurrentResourceCentre";
import useLocalPhotoSave from "../../../../hooks/useLocalPhotoSave";

// eslint-disable-next-line import/prefer-default-export
export const ClientSettings = (): React.ReactNode => {
  const dispatch = useDispatch();
  const resourceCentre = useCurrentResourceCentre();
  const { isSupported, folderName, selectFolder, clearFolder } = useLocalPhotoSave();
  // status
  const [defaultOpdFee, setDefaultOpdFee] = useState<string>(
    resourceCentre?.settings?.opdSettings?.defaultOpdFee ?? ""
  );
  const [defaultAdvice, setDefaultAdvice] = useState<string>(
    resourceCentre?.settings?.opdSettings?.defaultAdvice ?? ""
  );

  // helper functions
  const actions = bindActionCreators({ ...resourceCentreActions }, dispatch);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Client Queue Settings
        </Typography>
        <FormControlLabel
          sx={{ pl: 2 }}
          control={
            <Checkbox
              size="small"
              checked={resourceCentre?.settings?.queueSettings?.enableQueueSystem ?? false}
              onChange={async () => {
                const updatedRc = produce(resourceCentre, (draft) => {
                  if (!draft.settings.queueSettings) {
                    draft.settings.queueSettings = {
                      enableQueueSystem: false
                    };
                  }
                  draft.settings.queueSettings.enableQueueSystem = !(
                    resourceCentre?.settings?.queueSettings?.enableQueueSystem ?? false
                  );
                });

                actions.putResourceCentre(updatedRc);

                // if disabled delete client queue numbers
                if (!updatedRc?.settings?.queueSettings?.enableQueueSystem) {
                  await deleteClientQueueNumbers({
                    resourceCentreId: updatedRc.id
                  });
                }
              }}
            />
          }
          label={tl("clientSettings.enableQueueSystem")}
        />
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Client Sticker Settings
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="subtitle1" sx={{ minWidth: "150px" }}>
            Default OPD Fee:
          </Typography>
          <TextField
            size="small"
            type="number"
            variant="outlined"
            fullWidth
            placeholder="0.00"
            value={defaultOpdFee}
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => setDefaultOpdFee(e.target.value)}
            onBlur={() => {
              actions.putResourceCentre(
                produce(resourceCentre, (draft) => {
                  if (!draft?.settings?.opdSettings) {
                    draft.settings.opdSettings = { defaultOpdFee: "", defaultAdvice: "" };
                  }
                  draft.settings.opdSettings.defaultOpdFee = defaultOpdFee;
                })
              );
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="subtitle1" sx={{ minWidth: "150px" }}>
            Default Advice:
          </Typography>
          <TextField
            size="small"
            type="text"
            fullWidth
            variant="outlined"
            placeholder="Enter default advice"
            value={defaultAdvice}
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => setDefaultAdvice(e.target.value)}
            onBlur={() => {
              actions.putResourceCentre(
                produce(resourceCentre, (draft) => {
                  if (!draft?.settings?.opdSettings) {
                    draft.settings.opdSettings = { defaultOpdFee: "", defaultAdvice: "" };
                  }
                  draft.settings.opdSettings.defaultAdvice = defaultAdvice;
                })
              );
            }}
          />
        </Box>
      </Box>

      <Divider />

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Photo Settings
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={resourceCentre.settings.clientSettings.disableWebcamMirroring || false}
              />
            }
            label="Disable Webcam Mirroring"
            onChange={(_, checked) => {
              actions.putResourceCentre(
                produce(resourceCentre, (draft) => {
                  draft.settings.clientSettings.disableWebcamMirroring = checked;
                })
              );
            }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={resourceCentre.settings.clientSettings.saveClientPhotoLocally || false}
              />
            }
            label="Save Client Photo on PC When Photo is Taken"
            onChange={(_, checked) => {
              actions.putResourceCentre(
                produce(resourceCentre, (draft) => {
                  draft.settings.clientSettings.saveClientPhotoLocally = checked;
                })
              );
            }}
          />
        </Box>

        {resourceCentre.settings.clientSettings.saveClientPhotoLocally && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="subtitle1" sx={{ minWidth: "150px" }}>
              Folder to Save Client Images:
            </Typography>
            {folderName ? (
              <Typography variant="subtitle1" sx={{ minWidth: "150px" }}>
                {folderName}
              </Typography>
            ) : (
              <Button
                onClick={selectFolder}
                disabled={!isSupported}
                variant="outlined"
                size="small"
              >
                Choose Folder
              </Button>
            )}
            {folderName && (
              <Button color="error" onClick={clearFolder} variant="outlined" size="small">
                Clear Folder
              </Button>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
